// An image decoded off the main thread, ready to blit.
//
// `THREE.TextureLoader` hands back an HTMLImageElement and Chrome defers the
// decode until the upload asks for pixels, so the decode lands inside the one
// indivisible `texImage2D`: Mars's 2048x1024 map cost 26 to 46 ms there and
// Jezero's 1906x3102 orthoimage 50 ms. `createImageBitmap` moves the decode to
// a browser thread and leaves the upload a blit.
//
// One rule for every ImageBitmap in the Atlas: the flip, where one is wanted,
// is done at DECODE time and the texture always carries `flipY = false`. The
// WebGL unpack flip does not apply to an ImageBitmap source - Chrome reads back
// the same rows with UNPACK_FLIP_Y_WEBGL set either way - so `flipY = true`
// over one is a flag that does nothing. This loader flips, because the body map
// and the region orthoimage store their first row at v = 1; a pyramid tile's
// albedo does not, so `tileSource.ts` decodes it as it comes.

export async function loadFlippedBitmap(url: string, signal?: AbortSignal): Promise<ImageBitmap> {
  return decode(url, 'flipY', signal);
}

/**
 * The same decode with the file's own rows kept, for a texture that wants them.
 *
 * The whole-file plot's orthoimage is sampled at `flipY = false` over the rows
 * as the file stores them (src/atlas/dtmPlot.ts), so flipping it here would
 * turn the ground it draws upside down against the height field beside it.
 */
export async function loadBitmap(url: string, signal?: AbortSignal): Promise<ImageBitmap> {
  return decode(url, 'none', signal);
}

async function decode(
  url: string, orientation: ImageOrientation, signal?: AbortSignal,
): Promise<ImageBitmap> {
  const res = await fetch(url, signal ? { signal } : undefined);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return createImageBitmap(await res.blob(), {
    imageOrientation: orientation, premultiplyAlpha: 'none', colorSpaceConversion: 'none',
  });
}
